import { ParametersDapFormData } from "@/types/forms/bo/clientsParameters.form";
import { useGetCompanyParametersBo, useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ParameterWoodBox } from "./parameters/parameterWoodBox";
import { ParameterDap } from "./parameters/parameterDap";
import { ParameterItem } from "./parameters/parametersElem";
import { UpdatePaymentTermModal } from "./parameters/updatePaymentTermModal";
import { UpdateInsuranceModal } from "./parameters/updateInsuranceModal";
import { UpdateContactModal } from "./parameters/updateContactModal";
import { EllipsisVertical, Landmark, CalendarDays, Package } from "lucide-react";
import { UpdateStatusModal } from "./parameters/updateStatusModal";
import { UpdateBusinessNameModal } from "./parameters/updateBusinessNameModal";
import { useGetRibs } from "@/api/rib.api";
import { UpdateRibModal } from "./parameters/updateRibModal";
import { UpdateTarifModal } from "./parameters/updateTarifModal";
import { UpdateImporterModal } from "./parameters/updateImporterModal";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { useGetAllImporters } from "@/api/importer.api";
import { CompanyStatusEnum } from "@/constants/enums/companyStatus.enum";
import { useCompanyStatusOptions } from "@/hooks/useEnumOptions";

type ParametersProps = {
  company?: GetAllCompanyDto;
};

export const Parameters = ({ company }: ParametersProps) => {
  const { t } = useTranslation("boClients");
  const companyStatusOptions = useCompanyStatusOptions();
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { data: companyParameters } = useGetCompanyParametersBo(companyId);
  const { data: rib } = useGetRibs();
  const { data: importers } = useGetAllImporters();
  const { mutateAsync: updateCompanyParametersBO } = useUpdateCompanyParametersBO();

  const handleDapUpdate = (data: ParametersDapFormData) => {
    if (!companyParameters) return;
    updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
  };

  if (!companyParameters) return <StyledSpinner />;

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <ParameterItem
        icon={EllipsisVertical}
        title={t("clientParameters.businessNameTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.businessNameLabel"),
            value: company?.businessName || t("clientParameters.notAvailable"),
          },
        ]}
        modal={
          <UpdateBusinessNameModal
            closeModal={() => {}}
            businessName={company?.businessName || ""}
            title={t("clientParameters.businessNameModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={EllipsisVertical}
        title={t("clientParameters.statusTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.statusLabel"),
            value:
              companyStatusOptions.find((s) => s.value === companyParameters.status)?.label ||
              t("clientParameters.valueError"),
          },
        ]}
        modal={
          <UpdateStatusModal
            closeModal={() => {}}
            status={companyParameters.status as CompanyStatusEnum}
            title={t("clientParameters.statusModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={CalendarDays}
        title={t("clientParameters.insuranceTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.insuranceLabel"),
            value: companyParameters.optionInsuranceActive
              ? t("clientParameters.insuranceValue", {
                  percentage: companyParameters.insurancePercentage,
                })
              : t("clientParameters.defaultInsurance"),
          },
        ]}
        modal={
          <UpdateInsuranceModal
            closeModal={() => {}}
            insurance={companyParameters.insurancePercentage}
            title={t("clientParameters.insuranceModalTitle")}
          />
        }
      />
      <ParameterDap
        optionDAPActive={companyParameters.optionDAPActive}
        handleUpdate={handleDapUpdate}
      />
      <ParameterItem
        icon={CalendarDays}
        title={t("clientParameters.tarifTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.tarifLabel"),
            value: company?.tarif.label || t("clientParameters.valueError"),
          },
        ]}
        modal={
          <UpdateTarifModal
            closeModal={() => {}}
            tarifId={companyParameters.tarifId}
            title={t("clientParameters.tarifModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={Landmark}
        title={t("clientParameters.ribTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.ribLabel"),
            value:
              rib?.find((r) => r.id === companyParameters.ribId)?.shortenName ||
              t("clientParameters.valueError"),
          },
        ]}
        modal={
          <UpdateRibModal
            closeModal={() => {}}
            ribId={companyParameters.ribId}
            title={t("clientParameters.ribModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={Package}
        title={t("clientParameters.importerTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.importerLabel"),
            value:
              importers?.data?.find((i) => i.id === companyParameters.importerId)?.name ||
              t("clientParameters.undefinedImporter"),
          },
        ]}
        modal={
          <UpdateImporterModal
            closeModal={() => {}}
            importerId={companyParameters.importerId}
            title={t("clientParameters.importerModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={CalendarDays}
        title={t("clientParameters.paymentTermTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.paymentTermLabel"),
            value: companyParameters.paymentTerm
              ? companyParameters.paymentTerm.toString()
              : t("clientParameters.defaultPaymentTerm"),
          },
        ]}
        modal={
          <UpdatePaymentTermModal
            closeModal={() => {}}
            paymentTerm={companyParameters.paymentTerm}
            title={t("clientParameters.paymentTermModalTitle")}
          />
        }
      />
      <ParameterItem
        icon={CalendarDays}
        title={t("clientParameters.contactTitle")}
        valueItems={[
          {
            valueLabel: t("clientParameters.contactEmailLabel"),
            value: companyParameters.contactEmail,
          },
          {
            valueLabel: t("clientParameters.contactPhoneLabel"),
            value: companyParameters.contactPhoneNumber,
          },
        ]}
        modal={
          <UpdateContactModal
            closeModal={() => {}}
            contactEmail={companyParameters.contactEmail}
            contactPhoneNumber={companyParameters.contactPhoneNumber}
            title={t("clientParameters.contactModalTitle")}
          />
        }
      />
      <ParameterWoodBox companyId={companyId} />
    </div>
  );
};
